import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import { ReportIssue } from "@/components/ReportIssue";
import { Txt } from "@/components/Txt";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import InstallPrompt from "@/components/InstallPrompt";
import InstallAppButton from "@/components/InstallAppButton";
import { PreviewBanner } from "@/components/RolePreviewSwitcher";
import RolePreviewSwitcher from "@/components/RolePreviewSwitcher";
import MemberDashboard from "@/pages/dashboards/MemberDashboard";
import BoardDashboard from "@/pages/dashboards/BoardDashboard";
import BoardMeetings from "@/pages/governance/BoardMeetings";
import VotesApprovals from "@/pages/governance/VotesApprovals";
import MyTasks from "@/pages/governance/MyTasks";
import BoardDocuments from "@/pages/governance/BoardDocuments";
import MemberEvents from "@/pages/events/MemberEvents";
import MemberRewards from "@/pages/points/MemberRewards";
import TargetedMessages from "@/pages/messaging/TargetedMessages";
import UnifiedCalendar from "@/components/UnifiedCalendar";
import DigitalCard from "@/components/DigitalCard";
import MyMemberships from "@/components/MyMemberships";
import PaymentHistory from "@/pages/membership/PaymentHistory";
import MyAccount from "@/pages/membership/MyAccount";
import MyVolunteering from "@/pages/events/MyVolunteering";
import GardenPlots from "@/pages/gardens/GardenPlots";
import MyMessages from "@/components/MyMessages";
import JoinWizard from "@/pages/membership/JoinWizard";
import GivePage from "@/pages/donations/GivePage";
import ClaudeConnector from "@/components/ClaudeConnector";
import { LogOut, Pencil, Check } from "lucide-react";

const PortalComingSoon = ({ title, note }) => (
  <div data-testid={`portal-coming-soon-${title.toLowerCase()}`} className="mt-4">
    <h1 className="font-zilla text-2xl font-semibold mb-2">{title}</h1>
    <div className="bg-cream border border-ink/15 rounded-xl p-8 text-center">
      <div className="text-[15px] text-ink/70">{note}</div>
      <div className="mt-3 inline-block text-[12px] font-semibold px-3 py-1 rounded-full bg-gold/25 text-ink">Coming soon</div>
    </div>
  </div>
);

export default function AppLayout({ me }) {
  const { logout, fetchMe, persona } = useAuth();
  const { editMode, setEditMode } = useContent();
  const isMember = me.role.role_id === "general_member";
  const [tab, setTab] = useState("home");
  const [connectorOpen, setConnectorOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [meetingOpenId, setMeetingOpenId] = useState(null);
  const [setupState, setSetupState] = useState("checking"); // checking | needed | done
  const nav = me.nav || [];

  // First-run lock: a brand-new general member must finish the welcome + birthdate
  // setup before anything else in the app is reachable (no bottom nav, no navigation).
  useEffect(() => {
    let alive = true;
    // In preview ("Viewing as"), never drop into the real onboarding wizard for the
    // admin's own account — show a read-only simulation of the member app instead.
    if (!isMember || persona) { setSetupState("done"); return; }
    api.get("/membership/me").then((r) => { if (alive) setSetupState(r.data.member ? "done" : "needed"); })
      .catch(() => { if (alive) setSetupState("done"); });
    return () => { alive = false; };
  }, [isMember, persona]);

  if (isMember && setupState === "checking") {
    return <div className="min-h-screen bg-warm-cream flex items-center justify-center text-ink/60">Loading…</div>;
  }
  if (isMember && setupState === "needed") {
    return (
      <div className="min-h-screen bg-warm-cream flex flex-col" data-testid="setup-lock">
        <div className="bg-ink text-cream px-4 py-3 flex items-center gap-3">
          <img src="/logo.png" alt="" className="w-9 h-9" />
          <div className="font-zilla font-semibold leading-none">Rosemoor</div>
          <button onClick={logout} data-testid="setup-logout" className="ml-auto text-cream/70 hover:text-cream text-[13px] flex items-center gap-1"><LogOut className="w-4 h-4" /> Sign out</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4">
            <h1 className="font-zilla text-3xl font-semibold">Welcome, {(me.user.name || "there").split(" ")[0]}</h1>
            <p className="text-[16px] text-ink/70">Let's finish setting up your Rosemoor membership.</p>
          </div>
          <JoinWizard onDone={async () => { await fetchMe(); setSetupState("done"); }} />
        </div>
      </div>
    );
  }

  const SHORT = {
    board_governance: "Board", votes_approvals: "Votes", my_tasks: "Tasks",
    documents: "Docs", partnerships: "Partners", events: "Events", my_membership: "Membership", calendar: "Calendar",
    my_card: "My Card", payments: "Payments", volunteering: "Volunteer", my_profile: "Account",
    points_rewards: "Rewards", give: "Give", targeted_messages: "Messages",
  };
  const activeNav = nav.find((n) => n.id === tab);

  const renderTab = () => {
    if (tab === "home") {
      return isMember ? <MemberDashboard me={me} onNavigate={setTab} /> : <BoardDashboard me={me} onNavigate={setTab} />;
    }
    if (tab === "events") {
      return <MemberEvents />;
    }
    if (tab === "calendar") {
      return <UnifiedCalendar onOpenMeeting={(id) => { setMeetingOpenId(id); setTab("board_governance"); }} />;
    }
    if (tab === "points_rewards") {
      return <MemberRewards />;
    }
    if (tab === "targeted_messages") {
      return <TargetedMessages me={me} />;
    }
    if (tab === "my_card") {
      return (
        <div data-testid="my-card-page">
          <h1 className="font-zilla text-3xl font-semibold mb-1">My Card</h1>
          <p className="text-[16px] text-ink/70 mb-5">Your official Rosemoor digital membership card.</p>
          <DigitalCard />
          <MyMemberships />
        </div>
      );
    }
    if (tab === "payments") {
      return <PaymentHistory />;
    }
    if (tab === "volunteering") {
      return <MyVolunteering />;
    }
    if (tab === "my_profile") {
      return <MyAccount />;
    }
    if (tab === "garden_plots") {
      return <GardenPlots />;
    }
    if (tab === "messages") {
      return <MyMessages asPage />;
    }
    if (tab === "give") {
      return <GivePage prefill={{ name: me.user.name, email: me.user.email }} />;
    }
    if (!isMember) {
      switch (tab) {
        case "board_governance":
          return <BoardMeetings openId={meetingOpenId} />;
        case "votes_approvals":
          return <VotesApprovals />;
        case "my_tasks":
          return <MyTasks />;
        case "documents":
          return <BoardDocuments />;
        case "my_membership":
          return <MemberDashboard me={me} onNavigate={setTab} />;
        case "partnerships":
          return <PortalComingSoon title="Partnerships" note="Once the Partnerships module ships, your community partners and agreements appear here." />;
        default:
          break;
      }
    }
    return (
      <div className="bg-cream border border-ink/15 rounded-xl p-6 text-center mt-4">
        <Icon name={activeNav?.icon} className="w-8 h-8 text-maroon mx-auto mb-3" />
        <h2 className="font-zilla text-2xl font-semibold mb-1">{activeNav?.label}</h2>
        <p className="text-ink/60 text-[15px]">
          This is part of your account. It fills in when the matching module ships in a later phase.
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-warm-cream flex flex-col">
      <InstallPrompt />
      <PreviewBanner />
      <ClaudeConnector open={connectorOpen} onClose={() => setConnectorOpen(false)} />

      {/* App top bar */}
      <div className="bg-ink text-cream px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
        <img src="/logo.png" alt="" className="w-9 h-9" />
        <div className="min-w-0">
          <div className="font-zilla font-semibold leading-none">Rosemoor</div>
          <div className="text-[11px] text-cream/60 truncate">members.rosemoorchicago.org</div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {me.user.is_super_admin && (
            <button
              data-testid="edit-mode-toggle"
              onClick={() => setEditMode(!editMode)}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 min-h-[40px] text-[13px] font-semibold border ${editMode ? "bg-maroon text-cream border-maroon" : "border-cream/30 text-cream/90 hover:bg-cream/10"}`}
              title="Turn on to click any wording and edit it in place"
            >
              {editMode ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              <span className="hidden sm:inline">{editMode ? "Done" : "Edit text"}</span>
            </button>
          )}
          {me.user.is_super_admin && <RolePreviewSwitcher />}
          {me.is_super_admin && (
            <button
              data-testid="claude-connector-btn"
              onClick={() => setConnectorOpen(true)}
              title="Connect this account to Claude"
              className="flex items-center gap-1.5 rounded-lg px-3 py-2 min-h-[40px] text-[13px] font-semibold border border-cream/30 text-cream/90 hover:bg-cream/10"
            >
              <Icon name="Plug" className="w-4 h-4" /><span className="hidden sm:inline">Claude</span>
            </button>
          )}
          {me.user.picture ? (
            <img src={me.user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-olive text-ink font-bold flex items-center justify-center text-sm">
              {me.user.name?.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
          )}
          <button onClick={logout} aria-label="Sign out" data-testid="logout-button" className="p-2 text-cream/70 hover:text-cream">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 overflow-y-auto p-4 pb-28 max-w-md w-full mx-auto" data-testid="main-content">
        <div key={tab} className="animate-rise">
          {renderTab()}
        </div>
      </main>

      {/* Bottom tab bar */}
      {(() => {
        const items = [{ id: "home", icon: "LayoutDashboard", label: "Home" },
          ...nav.map((n) => ({ id: n.id, icon: n.icon, label: SHORT[n.id] || n.label }))];
        const MAX = 5;
        const primary = items.length > MAX ? items.slice(0, MAX - 1) : items;
        const overflow = items.length > MAX ? items.slice(MAX - 1) : [];
        const overflowActive = overflow.some((i) => i.id === tab);
        const label = (item) => (item.id === "home"
          ? <Txt id="nav.app.home" screen="Navigation">Home</Txt>
          : <Txt id={`nav.section.${item.id}`} screen="Navigation">{item.label}</Txt>);
        return (
          <>
            <nav className="fixed bottom-0 left-0 right-0 bg-cream border-t border-ink/15 flex z-20 max-w-md mx-auto" data-testid="app-bottom-nav">
              {primary.map((item) => (
                <button
                  key={item.id}
                  data-testid={`app-nav-${item.id}`}
                  onClick={() => { setTab(item.id); setMoreOpen(false); }}
                  className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[66px] text-[11px] ${tab === item.id ? "text-maroon font-semibold" : "text-ink/60"}`}
                >
                  <Icon name={item.icon} className="w-[26px] h-[26px]" />
                  <span className="truncate max-w-full px-0.5 leading-tight">{label(item)}</span>
                </button>
              ))}
              {overflow.length > 0 && (
                <button
                  data-testid="app-nav-more"
                  onClick={() => setMoreOpen(true)}
                  className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2.5 min-h-[66px] text-[11px] ${overflowActive || moreOpen ? "text-maroon font-semibold" : "text-ink/60"}`}
                >
                  <Icon name="MoreHorizontal" className="w-[26px] h-[26px]" />
                  <span className="leading-tight">More</span>
                </button>
              )}
            </nav>

            {moreOpen && (
              <div className="fixed inset-0 z-30" data-testid="more-sheet">
                <div className="absolute inset-0 bg-ink/40" onClick={() => setMoreOpen(false)} />
                <div className="absolute bottom-0 left-0 right-0 bg-cream rounded-t-2xl p-4 pb-8 max-w-md mx-auto animate-rise">
                  <div className="w-10 h-1 bg-ink/15 rounded-full mx-auto mb-4" />
                  <h3 className="font-zilla text-lg font-semibold mb-3 px-1">More</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {overflow.map((item) => (
                      <button
                        key={item.id}
                        data-testid={`more-nav-${item.id}`}
                        onClick={() => { setTab(item.id); setMoreOpen(false); }}
                        className={`flex flex-col items-center gap-2 rounded-xl p-4 border transition-colors ${tab === item.id ? "border-maroon bg-maroon/5 text-maroon" : "border-ink/12 text-ink/75 hover:border-maroon/40"}`}
                      >
                        <Icon name={item.icon} className="w-7 h-7" />
                        <span className="text-[12px] font-semibold text-center leading-tight">{item.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-ink/10">
                    <InstallAppButton variant="mobile-list" />
                    <ReportIssue screen={activeNav?.label || "App"} variant="mobile-list" />
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
